<template>
  <div class="address-block mt-4">
    <div class="d-flex align-center mb-2">
      <v-icon size="18" color="primary" class="mr-2">mdi-map-marker</v-icon>
      <span class="text-subtitle-2 font-weight-medium">{{ tl(title) }}</span>
    </div>

    <!-- "IF BUSINESS ADDRESS IS AVAILABLE" 토글 — 최상위(부모). CRS 주소 폼과 문구/동작 일치.
         체크(available)하면 아래 "same as registered" 옵션과 주소 입력란이 그 안에서 열리고,
         해제하면 주소를 비워 isBusinessAddressAvailable=false 로 전송된다. -->
    <v-checkbox
      v-if="availableToggle"
      v-model="isAvailable"
      :label="tl(availableToggle.label)"
      density="compact"
      color="primary"
      class="mb-2"
      :readonly="readonly"
    />

    <!-- 주소가 "있음" 일 때만 열리는 영역. availableToggle 이 있으면 들여쓰기로 하위임을 표시. -->
    <div v-show="addrAvailable" :class="{ 'nested-under-toggle': availableToggle }">
      <!-- Same As checkbox — 비즈니스 주소가 있을 때만, 등록주소와 같은지 선택 -->
      <v-checkbox
        v-if="sameAs"
        v-model="isSameAs"
        :label="tl(sameAs.label)"
        density="compact"
        color="primary"
        class="mb-2"
        :readonly="readonly"
      />

      <!-- LOCAL / FOREIGN — CRS 주소 폼과 동일한 전환 스위치.
           FOREIGN 이면 State/City/Post Code 를 받지 않는다(CRS 도 null 로 보낸다). -->
      <v-radio-group
        v-show="!isSameAs"
        :model-value="addressType"
        inline
        hide-details
        density="compact"
        class="mb-1"
        :readonly="readonly"
        @update:model-value="onAddressTypeChange"
      >
        <v-radio :label="tl('Local Address')" value="LOCAL" />
        <v-radio :label="tl('Foreign Address')" value="FOREIGN" />
      </v-radio-group>

      <div v-show="!isSameAs" class="field-grid">
        <FieldRenderer
          v-for="field in validatedFields"
          :key="field.id"
          :field="field"
          :model-value="getFieldValue(field.id)"
          :readonly="readonly"
          :ref-data="filteredRefData"
          :density="density"
          :country-value="getFieldValue('country')"
          :error-message="getFieldError(field.id)"
          :field-key="errorPrefix ? `${errorPrefix}${field.id}` : undefined"
          @update:model-value="setFieldValue(field.id, $event)"
        />
      </div>
    </div>

    <!-- Validation error summary -->
    <div v-if="showErrors && validationErrors.length > 0" class="mt-1">
      <div v-for="err in validationErrors" :key="err" class="text-error text-caption">
        {{ err }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useTranslation } from '@/utils/locales/messages';
import { useLanguageStore } from '@/shared/stores/languageStore';
import FieldRenderer from './FieldRenderer.vue';
import type { FieldDef, RefDataItem } from '../composables/useFormRenderer';

// Translation helper
const languageStore = useLanguageStore();
const { createSyncT } = useTranslation();
const t = createSyncT(languageStore.currentLanguage, { silent: true });
const tl = (text: string) => {
  if (!text) return '';
  const translated = t(`startTask.${text}`);
  return (translated && translated !== `startTask.${text}`) ? translated : text;
};

interface SameAsConfig {
  target: string;
  label: string;
  default?: boolean;
}

interface Props {
  addressId: string;
  title: string;
  modelValue: Record<string, any>;
  templateRef?: string;
  includeOptional?: boolean;
  defaultCountry?: string;
  sameAs?: SameAsConfig | null;
  // "IF BUSINESS ADDRESS IS AVAILABLE" 토글(CRS 대응). 있으면 체크박스 노출.
  // 체크=주소 있음(입력·검증), 체크 해제=주소 없음(isBusinessAddressAvailable=false).
  availableToggle?: { label: string; default?: boolean } | null;
  readonly?: boolean;
  refData?: Record<string, RefDataItem[]>;
  density?: 'compact' | 'comfortable';
  // Source address data for sameAs
  sourceAddress?: Record<string, any>;
  // Template resolver function from parent
  resolveTemplateFn?: (ref: string, includeOptional?: boolean) => FieldDef[];
  // Show validation errors
  showErrors?: boolean;
  // Field-level error map key prefix (e.g. applicationDetails._addr.reg.)
  errorPrefix?: string;
  fieldErrors?: Record<string, string>;
  // Director/Shareholder 주소는 office number 필수 아님
  requireOfficeNumber?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  templateRef: 'address',
  includeOptional: true,
  defaultCountry: 'MALAYSIA',
  sameAs: null,
  availableToggle: null,
  readonly: false,
  refData: () => ({}),
  density: 'compact',
  sourceAddress: () => ({}),
  resolveTemplateFn: undefined,
  showErrors: false,
  errorPrefix: '',
  fieldErrors: () => ({}),
  requireOfficeNumber: true,
});

const emit = defineEmits<{
  'update:modelValue': [value: Record<string, any>];
}>();

// 실제로 입력된 주소 필드가 있는지 — country 는 기본값이 자동 주입되므로 제외
function hasMeaningfulAddress(addr: Record<string, any> | undefined | null): boolean {
  if (!addr || typeof addr !== 'object') return false;
  return Object.entries(addr).some(([k, v]) => {
    if (k === 'country') return false;
    return v != null && String(v).trim() !== '';
  });
}

// sameAs 기본값: 저장된 biz 주소가 실제로 존재하면 unchecked 로 시작해서 그 값을 그대로 보여준다
const isSameAs = ref(
  (props.sameAs?.default ?? false) && !hasMeaningfulAddress(props.modelValue)
);

// "주소 있음(available)" 기본값:
//   ① 저장된 주소가 있으면 체크(그 값을 보여준다)
//   ② sameAs 가 켜져 시작하면 주소는 reg 복사로 존재하므로 available 도 체크(두 체크박스 정합)
//   ③ 그 외에는 availableToggle.default 를 따른다(설정상 기본 false = 주소 없음)
const isAvailable = ref(
  hasMeaningfulAddress(props.modelValue) || isSameAs.value || (props.availableToggle?.default ?? false)
);

// 비즈니스 주소가 "있음" 인지: availableToggle 이 없으면 항상 true,
// 있으면 체크됐을 때만 true(=하위 same-as 옵션/입력란을 연다).
const addrAvailable = computed(
  () => !props.availableToggle || isAvailable.value
);

// modelValue 가 async 로 늦게 들어오는 경우에도 한 번 재평가
const initialSyncDone = ref(false);
watch(
  () => props.modelValue,
  (val) => {
    if (initialSyncDone.value) return;
    if (hasMeaningfulAddress(val)) {
      isSameAs.value = false;
      if (props.availableToggle) isAvailable.value = true;
      initialSyncDone.value = true;
    }
  },
  { deep: true, immediate: true }
);

const allFields = computed(() => {
  if (props.resolveTemplateFn) {
    return props.resolveTemplateFn(props.templateRef, props.includeOptional);
  }
  return [];
});

/**
 * 주소 종류. CRS 는 라디오(LOCAL/FOREIGN)로 명시 전환하고 우리는 국가로 판단해 왔다 —
 * 화면에도 같은 스위치를 둬서 어느 쪽 주소를 넣는지 분명히 보이게 한다.
 */
const addressType = computed(() =>
  ((props.modelValue?.country || props.defaultCountry) === 'MALAYSIA' ? 'LOCAL' : 'FOREIGN'));

function onAddressTypeChange(v: any) {
  if (props.readonly) return;
  if (v === 'LOCAL') {
    emit('update:modelValue', { ...props.modelValue, country: 'MALAYSIA' });
  } else {
    // 해외로 바꾸면 말레이시아 전용 코드값은 의미가 없으므로 비운다.
    emit('update:modelValue', { ...props.modelValue, country: '', state: '', city: '', postcode: '' });
  }
}

/** Malaysia 전용 — 해외 주소는 state/city/postcode 미표시·미검증 */
const MALAYSIA_ONLY_FIELD_IDS = new Set(['state', 'city', 'postcode']);
const MALAYSIA_REQUIRED_FIELDS = ['state', 'city', 'postcode', 'street01'];

const visibleFields = computed((): FieldDef[] => {
  const isMalaysia = (props.modelValue?.country || props.defaultCountry) === 'MALAYSIA';
  return allFields.value.filter(f => {
    if (!props.includeOptional && f.optional) return false;
    if (!isMalaysia && MALAYSIA_ONLY_FIELD_IDS.has(f.id)) return false;
    return true;
  });
});

const validatedFields = computed((): FieldDef[] => {
  const isMalaysia = (props.modelValue?.country || props.defaultCountry) === 'MALAYSIA';
  return visibleFields.value.map(f => {
    let field = f;
    if (isMalaysia && MALAYSIA_REQUIRED_FIELDS.includes(f.id) && !f.required) {
      field = { ...f, required: true };
    }
    if (!props.requireOfficeNumber && f.id === 'officeNumber') {
      field = { ...field, required: false, optional: true };
    }
    return field;
  });
});

function getFieldError(fieldId: string): string | undefined {
  if (!props.showErrors || !props.errorPrefix) return undefined;
  return props.fieldErrors?.[`${props.errorPrefix}${fieldId}`];
}

// Validation: Malaysia 주소 — state/city/postcode; 공통 — street, office number
const validationErrors = computed((): string[] => {
  // sameAs(복사) 또는 "주소 없음"(availableToggle 체크 해제)이면 검증 생략
  if (isSameAs.value || (props.availableToggle && !isAvailable.value)) return [];
  const isMalaysia = (props.modelValue?.country || props.defaultCountry) === 'MALAYSIA';
  const errors: string[] = [];
  const val = props.modelValue || {};

  if (isMalaysia) {
    if (!val.state) errors.push(tl('State is required'));
    if (!val.city) errors.push(tl('City is required'));
    if (!val.postcode) errors.push(tl('Post Code is required'));
    else if (!isValidPostcode(val.postcode)) errors.push(tl('Post Code is invalid - please select from dropdown'));
  }
  if (!val.street01) errors.push(tl('Street address is required'));
  if (props.requireOfficeNumber && !String(val.officeNumber || '').trim()) {
    errors.push(tl('Office Number is required'));
  }

  return errors;
});

// Postcode 유효성 검사: refData의 postcodes에 존재하는 값인지
function isValidPostcode(value: string): boolean {
  if (!value) return false;
  const postcodes = props.refData?.postcodes;
  if (!postcodes || postcodes.length === 0) return true; // refData 없으면 검사 스킵
  return postcodes.some((p: any) => p.value === value);
}

// Validate 메서드 (부모에서 호출 가능)
function validate(): { valid: boolean; errors: string[] } {
  const errors = validationErrors.value;
  return { valid: errors.length === 0, errors };
}

defineExpose({ validate });

/**
 * 옵션 목록에서 저장값에 해당하는 **옵션 value** 를 찾는다.
 *
 * 저장된 주소가 항상 옵션 value 인 것은 아니다. T-Link 설립신청서는 state 를
 * 라벨(`W.P. KUALA LUMPUR`)로 들고 있는데 옵션 value 는 `WP_KUALA_LUMPUR` 다.
 * 그대로 필터를 걸면 도시 목록이 0건이 되고, v-select 가 라벨을 못 찾아
 * `KualaLumpur_WPKualaLumpur` 같은 **내부값이 화면에 그대로 노출**된다.
 * 그래서 value → label 순으로 느슨하게 대조해 정규화한다(구두점·공백 무시).
 */
const looseKey = (v: any) => String(v ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
function canonical(list: any[] | undefined, v: any): string {
  const s = String(v ?? '');
  if (!s || !Array.isArray(list) || !list.length) return s;
  if (list.some((o: any) => o.value === s)) return s;
  const k = looseKey(s);
  const hit = list.find((o: any) => looseKey(o.value) === k || looseKey(o.label) === k);
  return hit ? hit.value : s;
}

/** 주(state) 옵션 목록. refData 키가 `malaysianStates` 다(`states` 아님). */
const stateOptions = computed<any[]>(
  () => (props.refData as any)?.malaysianStates || (props.refData as any)?.states || [],
);

/**
 * 걸러 낸 목록에 **저장값에 해당하는 옵션**이 빠졌으면 도로 넣는다.
 *
 * 우편번호는 같은 번호가 여러 도시에 중복으로 들어 있다(11700 이 George Town 과
 * Gelugor 양쪽에 있다). T-Link 에서 고른 행이 여기서 고른 도시와 다르면 도시 필터에
 * 걸려 목록에서 사라지고, v-select 가 라벨을 못 찾아 `P11700_GeorgeTown` 같은
 * **내부 enum 이 화면에 그대로 노출**된다(2026-09-27 운영 확인).
 * 저장값 자체는 유효한 행이므로, 표시할 수 있도록 원본 목록에서 찾아 되살린다.
 */
function keepSelected(filtered: any[], all: any[] | undefined, raw: any): any[] {
  const v = canonical(all, raw);
  if (!v || !Array.isArray(all)) return filtered;
  if (filtered.some((o: any) => o.value === v)) return filtered;
  const hit = all.find((o: any) => o.value === v);
  return hit ? [...filtered, hit] : filtered;
}

// Cascading filter: state → cities, city → postcodes
const filteredRefData = computed(() => {
  const base = { ...props.refData };
  const state = canonical(stateOptions.value, props.modelValue?.state);
  const city = canonical(props.refData?.cities, props.modelValue?.city);

  if (state && base.cities) {
    base.cities = keepSelected(
      base.cities.filter((c: any) => c.state === state),
      props.refData?.cities,
      props.modelValue?.city,
    );
  }

  if (city && base.postcodes) {
    base.postcodes = base.postcodes.filter((p: any) => p.city === city);
  } else if (state && base.postcodes) {
    base.postcodes = base.postcodes.filter((p: any) => p.state === state);
  }
  if (base.postcodes) {
    base.postcodes = keepSelected(base.postcodes, props.refData?.postcodes, props.modelValue?.postcode);
  }

  return base;
});

/** 셀렉트가 항목을 찾을 수 있도록 저장값을 옵션 value 로 맞춰 준다(표시 전용). */
function optionsFor(fieldId: string): any[] | null {
  if (fieldId === 'state') return stateOptions.value;
  // city/postcode 는 상위 선택으로 걸러진 목록에서 찾아야 후보가 맞다.
  if (fieldId === 'city') return (filteredRefData.value as any)?.cities || null;
  if (fieldId === 'postcode') return (filteredRefData.value as any)?.postcodes || null;
  return null;
}

function getFieldValue(fieldId: string): any {
  const raw = (isSameAs.value && props.sourceAddress)
    ? (props.sourceAddress[fieldId] ?? props.modelValue[fieldId] ?? '')
    : (props.modelValue[fieldId] ?? '');
  const list = optionsFor(fieldId);
  return list ? canonical(list, raw) : raw;
}

function setFieldValue(fieldId: string, value: any) {
  const prev = props.modelValue;
  const updated = { ...prev, [fieldId]: value };

  // Cascading reset: parent change clears child fields
  if (fieldId === 'country' && value !== prev.country) {
    updated.state = '';
    updated.city = '';
    updated.postcode = '';
  }
  if (fieldId === 'state' && value !== prev.state) {
    updated.city = '';
    updated.postcode = '';
  }
  if (fieldId === 'city' && value !== prev.city) {
    updated.postcode = '';
    // Auto-set postcode if only one match for this city
    if (value && props.refData.postcodes) {
      const matching = props.refData.postcodes.filter((p: any) => p.city === value);
      if (matching.length === 1) {
        updated.postcode = matching[0].value;
      }
    }
  }

  emit('update:modelValue', updated);
}

// Initialize defaults
watch(() => props.defaultCountry, (val) => {
  if (val && !props.modelValue.country) {
    setFieldValue('country', val);
  }
}, { immediate: true });

// Sync sameAs
// 체크 시: reg 주소 복사
// 언체크 시: modelValue 유지 (저장된 biz 주소 그대로 표시) → 사용자가 자유롭게 편집
watch(isSameAs, (val) => {
  if (val) {
    if (props.availableToggle) isAvailable.value = true;   // 복사된 주소가 있으므로 available
    if (props.sourceAddress) emit('update:modelValue', { ...props.sourceAddress });
  } else if (props.availableToggle && !isAvailable.value) {
    // sameAs 해제 + available 아님 → 복사돼 있던 reg 주소를 비운다(no business address).
    emit('update:modelValue', { country: props.modelValue?.country || props.defaultCountry });
  }
});

// "주소 있음(available)" 체크 해제 시: sameAs 해제하고 주소를 비운다(country 는 기본값 유지).
//   street01 이 비어 payload 가 isBusinessAddressAvailable=false 로 보낸다.
watch(isAvailable, (val) => {
  if (!val) {
    if (isSameAs.value) isSameAs.value = false;   // 상호배타
    emit('update:modelValue', { country: props.modelValue?.country || props.defaultCountry });
  }
});

// Re-sync when source changes and isSameAs is true
// 단, biz 에 이미 의미있는 데이터가 있으면 덮어쓰지 않음 (저장된 Business Address 보호)
watch(() => props.sourceAddress, (newSrc) => {
  if (!isSameAs.value || !newSrc) return;
  if (hasMeaningfulAddress(props.modelValue)) return;
  emit('update:modelValue', { ...newSrc });
}, { deep: true });
</script>

<style scoped>
.address-block {
  border-left: 3px solid rgb(var(--v-theme-primary));
  padding-left: 12px;
}

/* availableToggle(부모) 하위임을 들여쓰기로 표시 */
.nested-under-toggle {
  margin-left: 28px;
  padding-left: 12px;
  border-left: 2px solid rgba(var(--v-theme-primary), 0.25);
}

.field-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px 12px;
}

@media (max-width: 960px) {
  .field-grid {
    grid-template-columns: 1fr;
  }
}
</style>
